import * as EXIF from "exif-js";
import * as pdfjs from "pdfjs-dist/legacy/build/pdf";

import { InputFile, InputImage, InputPdf } from "./file-classes";

/**
 * @jest-environment node
 */
import axios from 'axios';
import pdfjsWorker from "pdfjs-dist/legacy/build/pdf.worker.entry";

const resizeImageFile = async (file, { width, height, mimeType, quality }) => {
    const url = await getFileDataURL(file);
    const image = await createImage(url);
    const orientation = await getOrientation(file);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const filename = file.name || "unknown";
    const ext = mimeType === "image/jpeg" ? ".jpg" : ".png";
    const newWidth =
        image.width > image.height
            ? width
            : (height / image.height) * image.width;
    const newHeight =
        image.width > image.height
            ? (width / image.width) * image.height
            : height;

    // EXIF numbers - https://owl.phy.queensu.ca/~phil/exiftool/TagNames/EXIF.html for full spec:

    // 1 = Horizontal (normal)
    // 2 = Mirror horizontal
    // 3 = Horizontal Rotate 180
    // 4 = Mirror vertical
    // 5 = Mirror horizontal and rotate 270 CW
    // 6 = Vertical Rotate 90 CW
    // 7 = Mirror horizontal and rotate 90 CW
    // 8 = Vertical Rotate 270 CW
    if (orientation === 6 || orientation === 8) {
        canvas.width = newHeight;
        canvas.height = newWidth;
    } else {
        canvas.width = newWidth;
        canvas.height = newHeight;
    }
    if (orientation === 3) {
        // Landscape upside down
        ctx.translate(newWidth, newHeight);
        ctx.rotate((180 * Math.PI) / 180);
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    } else if (orientation === 6) {
        // Default portrait
        ctx.translate(newHeight, 0);
        ctx.rotate((90 * Math.PI) / 180);
        ctx.drawImage(image, 0, 0, canvas.height, canvas.width);
    } else if (orientation === 8) {
        // Upside down portrait
        ctx.translate(0, newWidth);
        ctx.rotate((270 * Math.PI) / 180);
        ctx.drawImage(image, 0, 0, canvas.height, canvas.width);
    } else {
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    }

    return canvasToFile(
        canvas,
        filename.substr(0, filename.lastIndexOf(".")) + ext,
        mimeType,
        quality
    );
};

const getFileDataURL = (file, reader = new FileReader()) =>
    new Promise((resolve, reject) => {
        reader.onload = readerEvent => resolve(readerEvent.target.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });

const getFileArrayBuffer = async file =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = event => resolve(event.target.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(file);
    });

const getPdfPageFromFile = async (file, pageNumber) => {
    pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

    const data = await getFileArrayBuffer(file);
    const pdf = await pdfjs.getDocument({ data }).promise;
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 1.0 });
    const canvas = document.createElement("canvas");
    const canvasContext = canvas.getContext("2d");

    canvas.height = viewport.height;
    canvas.width = viewport.width;
    await page.render({ canvasContext, viewport }).promise;
    return canvasToFile(
        canvas,
        file.name.substr(0, file.name.lastIndexOf(".")) + ".jpg",
        "image/jpeg"
    );
};

const isPasswordProtected = async file => {
    pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

    try {
        const data = await getFileArrayBuffer(file);
        await pdfjs.getDocument({ data }).promise;
    } catch (error) {
        if (error.name && error.name === "PasswordException") {
            return true;
        }
    }
    return false;
};

const createImage = url =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(image.error);
        image.src = url;
    });

const canvasToFile = (canvas, filename, mimeType, quality) =>
    new Promise(resolve => {
        canvas.toBlob(
            blob => {
                resolve(
                    new File([blob], filename, {
                        lastModified: new Date()
                    })
                );
            },
            mimeType,
            quality
        );
    });

const generateUniqueId = () => {
    return 'xxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3) | 0x8;

        return v.toString(16);
    });
}

const getOrientation = async image => {
    const ab = await getFileArrayBuffer(image);
    const data = EXIF.readFromBinaryFile(ab);
    const orientation = data && data.Orientation;
    return orientation || 1;
};

const getErrorMessage = (errorCode) =>
    errorCode === 9998 || errorCode === "illegal_extension" || errorCode === "invalid_file"
        ? "We found an issue with the file you tried to upload. " +
            "The file type is not permitted or the mime type does not match the file content."
        : "Something went wrong, please try again!";

const uploadFile = async (image, inputId) => {
    const [field, formId, id] = inputId.split("_");
    const data = new FormData();
    const gformUniqueId = generateUniqueId();
    let errorMessage = "";

    try {
        data.append('file', image);
        data.append(field + '_id', id);
        data.append('form_id', formId);
        data.append('original_filename', image.name);
        data.append('name', image.name);
        data.append('gform_unique_id', gformUniqueId);

        const result = await axios.post(process.env.REACT_APP_API_UPLOAD_FILE, data);
        if (result.data.status === "ok") {
            return result.data.data;
        }
        errorMessage = getErrorMessage(result.data.error.code);
    } catch (error) {
        errorMessage = getErrorMessage(error.response.data.error.code);
    }
    throw Error(errorMessage);
}

const generateFilePreview = async file => {
    const input = file instanceof InputFile ? file.toFile() : file;
    try {
        const pdf = new InputPdf(input);
        const image = new InputImage(input);
        switch (true) {
            case pdf.hasValidExtension(): {
                const page = new InputImage(await pdf.pageImage(1));
                return [await page.dataUrl(), ""];
            }
            case image.hasValidExtension():
                return [await image.dataUrl(), ""];
            default:
                return ["", "File preview"];
        }
    } catch (error) {
        console.error(error);
        return ["", `Unable to generate preview`];
    }
};

export {
    resizeImageFile,
    getFileDataURL,
    getPdfPageFromFile,
    createImage,
    canvasToFile,
    getOrientation,
    getFileArrayBuffer,
    isPasswordProtected,
    uploadFile,
    generateUniqueId,
    generateFilePreview
};
